import { clsx } from 'clsx'
import { ButtonHTMLAttributes, forwardRef, HTMLAttributes, ReactNode } from 'react'

export type BadgeType = 'pill-color' | 'badge-color' | 'badge-modern'
export type BadgeColor = 'gray' | 'brand' | 'error' | 'warning' | 'success' | 'orange'
export type BadgeSize = 'sm' | 'md' | 'lg'

type BadgeShape = 'pill' | 'badge'
type LeadingKind = 'dot' | 'icon-leading'
type TrailingKind = 'close' | 'icon-trailing'
type PaddingKind = 'none' | LeadingKind | TrailingKind

interface BadgeColorClasses {
    surface: string
    dot: string
    close: string
}

const COLOR_CLASSES: Record<BadgeColor, BadgeColorClasses> = {
    gray: {
        surface: 'bg-utility-neutral-50 border-utility-neutral-200 text-utility-neutral-700',
        dot: 'bg-utility-neutral-500',
        close: 'text-utility-neutral-400 hover:text-utility-neutral-500',
    },
    brand: {
        surface: 'bg-utility-brand-50 border-utility-brand-200 text-utility-brand-700',
        dot: 'bg-utility-brand-500',
        close: 'text-utility-brand-400 hover:text-utility-brand-500',
    },
    error: {
        surface: 'bg-utility-red-50 border-utility-red-200 text-utility-red-700',
        dot: 'bg-utility-red-500',
        close: 'text-utility-red-400 hover:text-utility-red-500',
    },
    warning: {
        surface: 'bg-utility-amber-50 border-utility-amber-200 text-utility-amber-700',
        dot: 'bg-utility-amber-500',
        close: 'text-utility-amber-400 hover:text-utility-amber-500',
    },
    success: {
        surface: 'bg-utility-green-50 border-utility-green-200 text-utility-green-700',
        dot: 'bg-utility-green-500',
        close: 'text-utility-green-400 hover:text-utility-green-500',
    },
    orange: {
        surface: 'bg-utility-orange-50 border-utility-orange-200 text-utility-orange-700',
        dot: 'bg-utility-orange-500',
        close: 'text-utility-orange-400 hover:text-utility-orange-500',
    },
}

const MODERN_SURFACE_CLASSES =
    'bg-background-bg-primary border-border-primary text-text-secondary shadow-xs'
const MODERN_CLOSE_CLASSES =
    'text-foreground-fg-quaternary hover:text-foreground-fg-quaternary_hover'

const SHAPE_BY_TYPE: Record<BadgeType, BadgeShape> = {
    'pill-color': 'pill',
    'badge-color': 'badge',
    'badge-modern': 'badge',
}

const RADIUS_CLASSES: Record<BadgeShape, Record<BadgeSize, string>> = {
    pill: {
        sm: 'rounded-radius-full',
        md: 'rounded-radius-full',
        lg: 'rounded-radius-full',
    },
    badge: {
        sm: 'rounded-radius-sm',
        md: 'rounded-radius-sm',
        lg: 'rounded-radius-md',
    },
}

const SIZE_CLASSES: Record<BadgeSize, string> = {
    sm: 'py-spacing-xxs text-xs',
    md: 'py-spacing-xxs text-sm',
    lg: 'py-spacing-xs text-sm',
}

const ICON_ONLY_PADDING_CLASSES: Record<BadgeSize, string> = {
    sm: 'p-[5px]',
    md: 'p-spacing-sm',
    lg: 'p-spacing-md',
}

interface PaddingSides {
    left: string
    right: string
}

const PADDING_CLASSES: Record<BadgeShape, Record<BadgeSize, Record<PaddingKind, PaddingSides>>> = {
    pill: {
        sm: {
            none: { left: 'pl-spacing-md', right: 'pr-spacing-md' },
            dot: { left: 'pl-spacing-sm', right: 'pr-spacing-md' },
            close: { left: 'pl-spacing-md', right: 'pr-[3px]' },
            'icon-trailing': { left: 'pl-spacing-md', right: 'pr-spacing-sm' },
            'icon-leading': { left: 'pl-spacing-sm', right: 'pr-spacing-md' },
        },
        md: {
            none: { left: 'pl-2.5', right: 'pr-2.5' },
            dot: { left: 'pl-spacing-md', right: 'pr-2.5' },
            close: { left: 'pl-2.5', right: 'pr-spacing-xs' },
            'icon-trailing': { left: 'pl-2.5', right: 'pr-spacing-md' },
            'icon-leading': { left: 'pl-spacing-md', right: 'pr-2.5' },
        },
        lg: {
            none: { left: 'pl-spacing-lg', right: 'pr-spacing-lg' },
            dot: { left: 'pl-2.5', right: 'pr-spacing-lg' },
            close: { left: 'pl-spacing-lg', right: 'pr-spacing-sm' },
            'icon-trailing': { left: 'pl-spacing-lg', right: 'pr-2.5' },
            'icon-leading': { left: 'pl-2.5', right: 'pr-spacing-lg' },
        },
    },
    badge: {
        sm: {
            none: { left: 'pl-spacing-sm', right: 'pr-spacing-sm' },
            dot: { left: 'pl-spacing-sm', right: 'pr-spacing-sm' },
            close: { left: 'pl-spacing-sm', right: 'pr-[3px]' },
            'icon-trailing': { left: 'pl-spacing-md', right: 'pr-spacing-sm' },
            'icon-leading': { left: 'pl-spacing-sm', right: 'pr-spacing-md' },
        },
        md: {
            none: { left: 'pl-spacing-md', right: 'pr-spacing-md' },
            dot: { left: 'pl-spacing-md', right: 'pr-spacing-md' },
            close: { left: 'pl-spacing-md', right: 'pr-spacing-xs' },
            'icon-trailing': { left: 'pl-spacing-md', right: 'pr-spacing-sm' },
            'icon-leading': { left: 'pl-spacing-sm', right: 'pr-spacing-md' },
        },
        lg: {
            none: { left: 'pl-2.5', right: 'pr-2.5' },
            dot: { left: 'pl-2.5', right: 'pr-2.5' },
            close: { left: 'pl-2.5', right: 'pr-spacing-sm' },
            'icon-trailing': { left: 'pl-2.5', right: 'pr-spacing-md' },
            'icon-leading': { left: 'pl-spacing-md', right: 'pr-2.5' },
        },
    },
}

const GAP_CLASSES: Record<BadgeSize, Record<PaddingKind, string>> = {
    sm: {
        none: 'gap-spacing-xs',
        dot: 'gap-spacing-xs',
        close: 'gap-spacing-xxs',
        'icon-trailing': 'gap-spacing-xxs',
        'icon-leading': 'gap-spacing-xxs',
    },
    md: {
        none: 'gap-spacing-xs',
        dot: 'gap-spacing-sm',
        close: 'gap-spacing-xxs',
        'icon-trailing': 'gap-spacing-xs',
        'icon-leading': 'gap-spacing-xs',
    },
    lg: {
        none: 'gap-spacing-xs',
        dot: 'gap-spacing-sm',
        close: 'gap-spacing-xxs',
        'icon-trailing': 'gap-spacing-xs',
        'icon-leading': 'gap-spacing-xs',
    },
}

const getLeadingKind = (dot: boolean, hasLeadingContent: boolean): LeadingKind | undefined => {
    if (dot) return 'dot'
    if (hasLeadingContent) return 'icon-leading'
    return undefined
}

const getTrailingKind = (
    dismissible: boolean,
    hasTrailingIcon: boolean,
): TrailingKind | undefined => {
    if (dismissible) return 'close'
    if (hasTrailingIcon) return 'icon-trailing'
    return undefined
}

const getPaddingClasses = (
    shape: BadgeShape,
    size: BadgeSize,
    leading: LeadingKind | undefined,
    trailing: TrailingKind | undefined,
    iconOnly: boolean,
): string => {
    if (iconOnly) return ICON_ONLY_PADDING_CLASSES[size]

    const matrix = PADDING_CLASSES[shape][size]
    const leftKind: PaddingKind = leading ?? trailing ?? 'none'
    const rightKind: PaddingKind = trailing ?? leading ?? 'none'

    return clsx(matrix[leftKind].left, matrix[rightKind].right)
}

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
    type?: BadgeType
    color?: BadgeColor
    size?: BadgeSize
    dot?: boolean
    iconLeading?: ReactNode
    iconTrailing?: ReactNode
    avatar?: ReactNode
    flag?: ReactNode
    onClose?: () => void
    iconOnly?: boolean
}

interface DismissButtonProps extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'color'
> {
    onClose: () => void
    colorClassName: string
}

const DismissButton = forwardRef<HTMLButtonElement, DismissButtonProps>(
    ({ onClose, colorClassName, className, ...props }, ref) => (
        <button
            aria-label="Remove"
            {...props}
            ref={ref}
            type="button"
            onClick={onClose}
            className={clsx(
                'inline-flex shrink-0 items-center justify-center rounded-radius-xs p-spacing-xxs transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                colorClassName,
                className,
            )}>
            <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path
                    d="M9 3L3 9M3 3L9 9"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    ),
)

DismissButton.displayName = 'DismissButton'

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
    (
        {
            type = 'pill-color',
            color = 'gray',
            size = 'md',
            dot = false,
            iconLeading,
            iconTrailing,
            avatar,
            flag,
            onClose,
            iconOnly = false,
            className,
            children,
            ...props
        },
        ref,
    ) => {
        const isModern = type === 'badge-modern'
        const shape = SHAPE_BY_TYPE[type]
        const palette = COLOR_CLASSES[color]

        const leadingKind = getLeadingKind(
            dot,
            Boolean(iconLeading) || Boolean(avatar) || Boolean(flag),
        )
        const trailingKind = getTrailingKind(Boolean(onClose), Boolean(iconTrailing))
        const gapKind: PaddingKind = leadingKind ?? trailingKind ?? 'none'

        const surfaceClasses = isModern ? MODERN_SURFACE_CLASSES : palette.surface
        const closeClasses = isModern ? MODERN_CLOSE_CLASSES : palette.close

        if (
            process.env.NODE_ENV === 'development' &&
            iconOnly &&
            !props['aria-label'] &&
            !props['aria-labelledby']
        ) {
            console.error('Badge: iconOnly requires an accessible label via aria-label')
        }

        return (
            <span
                {...props}
                ref={ref}
                className={clsx(
                    'inline-flex shrink-0 items-center whitespace-nowrap border font-body font-medium',
                    RADIUS_CLASSES[shape][size],
                    SIZE_CLASSES[size],
                    GAP_CLASSES[size][gapKind],
                    getPaddingClasses(shape, size, leadingKind, trailingKind, iconOnly),
                    surfaceClasses,
                    className,
                )}>
                {dot && (
                    <span
                        className="inline-flex h-2 w-2 shrink-0 items-center justify-center"
                        aria-hidden="true">
                        <span className={clsx('h-1.5 w-1.5 rounded-radius-full', palette.dot)} />
                    </span>
                )}

                {avatar && (
                    <span className="inline-flex h-4 w-4 shrink-0 overflow-hidden rounded-radius-full">
                        {avatar}
                    </span>
                )}

                {flag && (
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center overflow-hidden rounded-radius-full">
                        {flag}
                    </span>
                )}

                {iconLeading && (
                    <span
                        className="inline-flex h-3 w-3 shrink-0 items-center justify-center"
                        aria-hidden="true">
                        {iconLeading}
                    </span>
                )}

                {!iconOnly && children}

                {iconTrailing && (
                    <span
                        className="inline-flex h-3 w-3 shrink-0 items-center justify-center"
                        aria-hidden="true">
                        {iconTrailing}
                    </span>
                )}

                {onClose && <DismissButton onClose={onClose} colorClassName={closeClasses} />}
            </span>
        )
    },
)

Badge.displayName = 'Badge'
